import { v4 as uuid } from "uuid";

export type Structure = {
  id: string;
  name: string;
  description?: string | null;
  tags: string[];
  structure_type_key: string;
  share_enabled?: boolean;
  share_token?: string | null;
  edit_password_enabled?: boolean;
  version_int?: number;
};

export type StructureType = {
  id: string;
  key: string;
  label: string;
  icon?: string | null;
  description?: string | null;
  config: {
    defaultNodeTypeKey?: string;
    availableLayouts?: string[];
    features?: {
      contacts?: boolean;
      links?: boolean;
      notes?: boolean;
      customFields?: boolean;
      decisionBranching?: boolean;
      swimlanes?: boolean;
    };
    nodeFieldsSchema?: Record<string, any>;
    edgeTypes?: string[];
    explorerDefaults?: {
      showContactsExplorer?: boolean;
      showLinksPanel?: boolean;
    };
  };
  is_system: boolean;
  created_at: string;
  updated_at: string;
};

export type AuthResponse = {
  access_token: string;
  token_type: string;
  expires_at: string;
  permissions: {
    can_view_private: boolean;
    can_edit: boolean;
  };
};

export type NodeTypeTemplate = {
  id: string;
  key: string;
  label: string;
  default_color: string;
  default_text_color: string;
  shape: string;
  sort_order: number;
  is_archived: boolean;
  structure_type_key?: string | null;
  created_at: string;
  updated_at: string;
};

export type AvatarMeta = {
  x?: number;
  y?: number;
  zoom?: number;
};

export type Contact = {
  id?: string | number;
  name: string;
  role?: string | null;
  type?: string | null;
  contact_type?: string | null;
  avatar_url?: string | null;
  avatar_meta?: AvatarMeta | null;
};

export type StructureContact = {
  id: string;
  structure_id: string;
  name: string;
  role?: string | null;
  contact_type: string; // primary, backup, external
  email?: string | null;
  phone?: string | null;
  signal?: string | null;
  matrix?: string | null;
  telegram?: string | null;
  website?: string | null;
  notes?: string | null;
  tags: string[];
  custom_fields: Record<string, any>;
  sort_order: number;
  avatar_url?: string | null;
  avatar_meta?: AvatarMeta | null;
  is_board_member: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
};

export type Link = {
  id?: string | number;
  url: string;
  title?: string | null;
  description?: string | null;
};

export type Note = { id?: string | number; text: string };

export type Position = { x: number; y: number };

export type NodeData = {
  id: string;
  type: string;
  label: string;
  description?: string | null;
  color?: string | null;
  text_color?: string | null;
  shape?: string; // rectangle, diamond, circle, ellipse
  position: Position;
  x?: number;
  y?: number;
  inactive: boolean;
  constituted: boolean;
  collapsed: boolean;
  contacts: Contact[];
  contact_ids: string[];
  links: Link[];
  notes: Note[];
  contact_roles?: Record<string, string>;
  custom_data?: Record<string, any>;
  highlighted?: boolean;
  hiddenDescendantCount?: number; // Count of hidden descendants when collapsed
  contactsCount?: number;
  boardContactsCount?: number;
  groupContactsCount?: number;
  linksCount?: number;
  notesCount?: number;
};

export type EdgeData = {
  id: string;
  source: string;
  target: string;
  source_handle?: string | null;
  target_handle?: string | null;
  arrow_style?: "default" | "reverse" | "both" | "none";
  line_style?: "bezier" | "straight" | "step" | "smoothstep";
  label?: string;
  color?: string;
  style?: "solid" | "dashed";
  routing?: {
    mode?: "auto" | "manual";
    bendOffset?: number;
  };
};

export type GraphData = {
  structure_id?: string;
  nodes: NodeData[];
  edges: EdgeData[];
};

export type ExportPayload = {
  version: number;
  structure: {
    id: string;
    name: string;
    description?: string | null;
    tags: string[];
    settings?: Record<string, unknown>;
  };
  nodes: NodeData[];
  edges: EdgeData[];
};

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

// Auth token storage
function getAuthToken(): string | null {
  return localStorage.getItem("atlas_access_token");
}

function setAuthToken(token: string) {
  localStorage.setItem("atlas_access_token", token);
}

function clearAuthToken() {
  localStorage.removeItem("atlas_access_token");
}

async function http<T>(url: string, options: RequestInit = {}): Promise<T> {
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
  };

  // Add auth token if available
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_URL}${url}`, {
    headers,
    credentials: "include", // Include cookies for httpOnly cookie auth
    ...options,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || res.statusText);
  }

  // Handle empty/204 responses gracefully
  const contentLength = res.headers.get("content-length");
  if (res.status === 204 || contentLength === "0") {
    return undefined as T;
  }

  const text = await res.text();
  if (!text) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export const api = {
  listStructures: () => http<{ items: Structure[] }>("/api/structures"),
  createStructure: (payload: { name: string; description?: string; tags?: string[]; structure_type_key?: string }) =>
    http<Structure>("/api/structures", { method: "POST", body: JSON.stringify(payload) }),
  updateStructure: (id: string, payload: Partial<Pick<Structure, "name" | "description">>) =>
    http<Structure>(`/api/structures/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  getStructure: (id: string) => http<Structure>(`/api/structures/${id}`),
  getGraph: (id: string) => http<GraphData>(`/api/structures/${id}/graph`),
  saveGraph: (id: string, graph: GraphData) =>
    http<GraphData>(`/api/structures/${id}/graph`, { method: "PUT", body: JSON.stringify(graph) }),
  deleteStructure: (id: string) => http<void>(`/api/structures/${id}`, { method: "DELETE" }),
  exportStructure: (id: string) => http<ExportPayload>(`/api/structures/${id}/export`),
  importStructure: (id: string, payload: ExportPayload) =>
    http<ExportPayload>(`/api/structures/${id}/import`, { method: "POST", body: JSON.stringify(payload) }),
  exportBackup: (id: string) => http<any>(`/api/backup/${id}`),
  importBackup: (payload: any, mode: "create_new" | "overwrite" = "create_new", targetId?: string, dryRun?: boolean) => {
    const params = new URLSearchParams();
    params.set("mode", mode);
    if (targetId) params.set("target_structure_id", targetId);
    if (dryRun) params.set("dry_run", "true");
    return http<any>(`/api/backup?${params.toString()}`, { method: "POST", body: JSON.stringify(payload) });
  },
  enableShare: (id: string) => http<{ share_token: string; share_enabled: boolean }>(`/api/structures/${id}/share/enable`, { method: "POST" }),
  disableShare: (id: string) =>
    http<{ share_token: string | null; share_enabled: boolean; edit_password_enabled?: boolean; generated_password?: string }>(
      `/api/structures/${id}/share/disable`,
      { method: "POST" }
    ),
  getShared: (token: string) => http<ExportPayload>(`/api/share/${token}`),

  // Auth & password management
  authenticate: async (id: string, password: string): Promise<AuthResponse> => {
    const response = await http<AuthResponse>(`/api/structures/${id}/auth`, {
      method: "POST",
      body: JSON.stringify({ password }),
    });
    // Store token in localStorage
    setAuthToken(response.access_token);
    return response;
  },
  setPassword: (id: string, password: string) =>
    http<{ edit_password_enabled: boolean; message: string }>(`/api/structures/${id}/password/set`, {
      method: "POST",
      body: JSON.stringify({ password }),
    }),
  removePassword: (id: string) =>
    http<{ edit_password_enabled: boolean; message: string }>(`/api/structures/${id}/password/remove`, { method: "POST" }),
  clearToken: () => clearAuthToken(),

  // Template management
  listTemplates: (includeArchived = false, structureTypeKey?: string, structureId?: string) => {
    const params = new URLSearchParams();
    params.set("include_archived", String(includeArchived));
    if (structureId) {
      params.set("structureId", structureId);
    } else if (structureTypeKey) {
      params.set("structureTypeKey", structureTypeKey);
    }
    return http<NodeTypeTemplate[]>(`/api/templates?${params.toString()}`);
  },
  getTemplate: (id: string) =>
    http<NodeTypeTemplate>(`/api/templates/${id}`),
  createTemplate: (payload: Omit<NodeTypeTemplate, "id" | "is_archived" | "created_at" | "updated_at">) =>
    http<NodeTypeTemplate>("/api/templates", { method: "POST", body: JSON.stringify(payload) }),
  updateTemplate: (id: string, payload: Partial<Pick<NodeTypeTemplate, "label" | "default_color" | "default_text_color" | "shape" | "sort_order">>) =>
    http<NodeTypeTemplate>(`/api/templates/${id}`, { method: "PATCH", body: JSON.stringify(payload) }),
  archiveTemplate: (id: string) =>
    http<NodeTypeTemplate>(`/api/templates/${id}/archive`, { method: "POST" }),
  unarchiveTemplate: (id: string) =>
    http<NodeTypeTemplate>(`/api/templates/${id}/unarchive`, { method: "POST" }),

  // Structure Types management
  listStructureTypes: () =>
    http<{ structure_types: StructureType[] }>("/api/structure-types"),
  getStructureType: (key: string) =>
    http<StructureType>(`/api/structure-types/${key}`),
  createStructureType: (payload: Omit<StructureType, "id" | "created_at" | "updated_at">) =>
    http<StructureType>("/api/structure-types", { method: "POST", body: JSON.stringify(payload) }),
  updateStructureType: (key: string, payload: Partial<Pick<StructureType, "label" | "icon" | "description" | "config">>) =>
    http<StructureType>(`/api/structure-types/${key}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteStructureType: (key: string) =>
    http<void>(`/api/structure-types/${key}`, { method: "DELETE" }),

  // Template Visibility management (per structure)
  getStructureTemplateVisibility: (structureId: string) =>
    http<Array<NodeTypeTemplate & { is_visible: boolean }>>(`/api/structures/${structureId}/template-visibility`),
  updateStructureTemplateVisibility: (structureId: string, updates: Array<{ template_id: string; is_visible: boolean }>) =>
    http<{ message: string }>(`/api/structures/${structureId}/template-visibility`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    }),

  // Structure Contacts management
  listContacts: (structureId: string, includeArchived = false) => {
    const params = new URLSearchParams();
    params.set("include_archived", String(includeArchived));
    return http<{ contacts: StructureContact[]; total: number }>(`/api/structures/${structureId}/contacts?${params.toString()}`);
  },
  getContact: (structureId: string, contactId: string) =>
    http<StructureContact>(`/api/structures/${structureId}/contacts/${contactId}`),
  createContact: (structureId: string, payload: Omit<StructureContact, "id" | "structure_id" | "is_archived" | "created_at" | "updated_at">) =>
    http<StructureContact>(`/api/structures/${structureId}/contacts`, { method: "POST", body: JSON.stringify(payload) }),
  updateContact: (structureId: string, contactId: string, payload: Partial<Omit<StructureContact, "id" | "structure_id" | "created_at" | "updated_at">>) =>
    http<StructureContact>(`/api/structures/${structureId}/contacts/${contactId}`, { method: "PATCH", body: JSON.stringify(payload) }),
  deleteContact: (structureId: string, contactId: string) =>
    http<void>(`/api/structures/${structureId}/contacts/${contactId}`, { method: "DELETE" }),
  archiveContact: (structureId: string, contactId: string) =>
    http<StructureContact>(`/api/structures/${structureId}/contacts/${contactId}/archive`, { method: "POST" }),
  unarchiveContact: (structureId: string, contactId: string) =>
    http<StructureContact>(`/api/structures/${structureId}/contacts/${contactId}/unarchive`, { method: "POST" }),
};

export { setAuthToken, getAuthToken, clearAuthToken };

export const blankNode = (): NodeData => ({
  id: uuid(),
  type: "gruppe",
  label: "Neuer Knoten",
  description: "",
  position: { x: 100, y: 100 },
  color: "#2563eb",
  text_color: "#ffffff",
  inactive: false,
  constituted: false,
  collapsed: false,
  contacts: [],
  contact_ids: [],
  contact_roles: {},
  links: [],
  notes: [],
  custom_data: {},
});
